// src/data/requestTasks.ts
// ------------------------
// Repository for the `request_tasks` table — the staff work-queue rows
// (responder to-do list + picker queue). Owns the table name, the shared column
// list, the responder-vs-picker type split, and the row→app shape. Typed via
// getTypedClient().

import type { Database } from "../types/database.generated";
import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

export type RequestTaskRow = Database["public"]["Tables"]["request_tasks"]["Row"];
type RequestTaskInsert = Database["public"]["Tables"]["request_tasks"]["Insert"];
type RequestTaskUpdate = Database["public"]["Tables"]["request_tasks"]["Update"];

// Picker-only task types; everything else is a responder task. `top_up` is the Stock
// Top-up picker task (one per SO) — added so top-up SOs surface in the Pickers list and
// are excluded from the Responders list (the exclusion filters below hard-code this set).
export const PICKER_TASK_TYPES = ["device", "stationery_qld", "top_up"];

// The Stock-Top-up staff queue types (TopUpTaskListPage). `top_up_approval` = the
// per-submission approval task; `top_up_comment` = a DFV-Service comment on a top-up
// request, routed to the Top-up queue instead of Responders (2026-3076, 15 Sep 2026).
export const TOP_UP_TASK_TYPES = ["top_up_approval", "top_up_comment"];

// Types that are NOT responder tasks — the picker queue PLUS the top-up queue. The
// Responders list / badges / cascade all exclude exactly this set. ONE source of truth
// (it was hard-coded in 5 places) so a new queue type can never drift across the copies.
export const NON_RESPONDER_TYPES = [...PICKER_TASK_TYPES, ...TOP_UP_TASK_TYPES];
const NON_RESPONDER_IN = `(${NON_RESPONDER_TYPES.map((t) => `"${t}"`).join(",")})`;

// The columns the task lists (responder + picker) render.
const TASK_LIST_COLS =
  "id, request_id, ref_no, shelter_name, shelter_code, state, type, description, so_number, assigned_to, assigned_to_id, assigned_at, completed_at, release_note, task_notes, created_at";

// --- counts (header badges) ---

// Open responder tasks (picker-only types excluded). null = no client / error.
// `completed_at IS NULL` matches the list's "unactioned" card, and the DRAFT-ref
// exclusion matches ToDoPage hiding draft-linked rows — so the badge equals the list
// instead of drifting above it (findings 2026-08-21 §1.1 / B16). A near-impossible
// residual remains: a real-ref task whose request is CURRENTLY Draft (the list catches
// that via its live requests join; a count(*) can't without a join) — not worth a join.
export async function countResponderTasks(): Promise<number | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { count, error } = await c
    .from("request_tasks")
    .select("*", { count: "exact", head: true })
    .is("completed_at", null)
    .not("type", "in", NON_RESPONDER_IN)
    .not("ref_no", "like", "DRAFT%");
  if (error) {
    logger.warn("countResponderTasks failed:", error.message);
    return null;
  }
  return count;
}

// Open responder tasks for ONE request (picker-only types excluded). Used by the
// request-detail exit prompt to know whether there's anything to offer clearing.
// When `assignedToId` is given, counts only tasks assigned to that user — the exit
// prompt passes the current user's id so it only offers to clear a task the
// responder actually holds (Actioned is assignee-only). Returns 0 on no client /
// error (treated as "nothing open" — the prompt simply won't fire, never a false
// positive).
export async function countOpenResponderTasks(
  requestId: number | string,
  assignedToId?: string | null
): Promise<number> {
  const c = getTypedClient();
  if (!c) return 0;
  let q = c
    .from("request_tasks")
    .select("*", { count: "exact", head: true })
    .eq("request_id", requestId as number)
    .is("completed_at", null)
    .not("type", "in", NON_RESPONDER_IN);
  if (assignedToId) q = q.eq("assigned_to_id", assignedToId);
  const { count, error } = await q;
  if (error) {
    logger.warn("countOpenResponderTasks failed:", error.message);
    return 0;
  }
  return count ?? 0;
}

// Open picker tasks. null = no client / error. Draft-ref tasks excluded so the badge
// matches the Pickers list (which hides draft-linked rows) — reconciles finding B16 at
// the query level. Same completed_at + DRAFT-residual notes as countResponderTasks.
export async function countPickerTasks(): Promise<number | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { count, error } = await c
    .from("request_tasks")
    .select("*", { count: "exact", head: true })
    .is("completed_at", null)
    .in("type", PICKER_TASK_TYPES)
    .not("ref_no", "like", "DRAFT%");
  if (error) {
    logger.warn("countPickerTasks failed:", error.message);
    return null;
  }
  return count;
}

// --- list reads ---

// All responder tasks, newest first. null on error / no client.
export async function listResponderTasks(): Promise<RequestTaskRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("request_tasks")
    .select(TASK_LIST_COLS)
    .not("type", "in", NON_RESPONDER_IN)
    .order("created_at", { ascending: false });
  if (error) {
    logger.warn("listResponderTasks failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as RequestTaskRow[];
}

// All picker tasks, OLDEST first (the Pickers queue is worked oldest-first so the
// longest-waiting orders sit at the top). null on error / no client.
export async function listPickerTasks(): Promise<RequestTaskRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("request_tasks")
    .select(TASK_LIST_COLS)
    .in("type", PICKER_TASK_TYPES)
    .order("created_at", { ascending: true });
  if (error) {
    logger.warn("listPickerTasks failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as RequestTaskRow[];
}

// All Stock-Top-up queue tasks — approval tasks (one per submitted top-up) AND
// `top_up_comment` tasks (a DFV-Service comment on a top-up, 2026-3076), newest first.
// Its own queue — excluded from BOTH the responder and picker lists (see the exclusions
// above / PICKER_TASK_TYPES). null on error / no client.
export async function listTopUpApprovalTasks(): Promise<RequestTaskRow[] | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("request_tasks")
    .select(TASK_LIST_COLS)
    .in("type", TOP_UP_TASK_TYPES)
    .order("created_at", { ascending: false });
  if (error) {
    logger.warn("listTopUpApprovalTasks failed:", error.message);
    return null;
  }
  return (data ?? []) as unknown as RequestTaskRow[];
}

// The top-up approval task for a request (newest), or null. Used by the approval view's
// assignment gate. null on error / no client / none.
export async function getTopUpApprovalTask(
  requestId: number | string
): Promise<RequestTaskRow | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("request_tasks")
    .select(TASK_LIST_COLS)
    .eq("request_id", requestId as number)
    .eq("type", "top_up_approval")
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) {
    logger.warn("getTopUpApprovalTask failed:", error.message);
    return null;
  }
  return data && data.length > 0 ? (data[0] as unknown as RequestTaskRow) : null;
}

// Open (uncompleted) top-up approval tasks — the nav badge count. null on error / no client.
export async function countTopUpApprovalTasks(): Promise<number | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { count, error } = await c
    .from("request_tasks")
    .select("*", { count: "exact", head: true })
    .in("type", TOP_UP_TASK_TYPES)
    .is("completed_at", null);
  if (error) {
    logger.warn("countTopUpApprovalTasks failed:", error.message);
    return null;
  }
  return count;
}

// Mark a request's top-up approval task Actioned (on Confirm & Send). Idempotent
// (only touches an open task). Returns the updated row(s). Best-effort.
export async function completeTopUpApprovalTask(
  requestId: number | string,
  completedAt: string
): Promise<{ data: RequestTaskRow[] | null; error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  const { data, error } = await c
    .from("request_tasks")
    .update({ completed_at: completedAt } as RequestTaskUpdate)
    .eq("request_id", requestId as number)
    .eq("type", "top_up_approval")
    .is("completed_at", null)
    .select();
  if (error) logger.warn("completeTopUpApprovalTask failed:", error.message);
  return { data: (data ?? []) as unknown as RequestTaskRow[], error };
}

// The active (uncompleted) take-on-lockable task for a request, newest first.
// Lockable types include new_comment (18 Jul 2026): opening a comment-only task
// now also requires the responder to claim (take on) the request.
export async function findActiveLockTask(
  requestId: number | string
): Promise<RequestTaskRow | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("request_tasks")
    .select("id, assigned_to, assigned_to_id, assigned_at, type, completed_at")
    .eq("request_id", requestId as number)
    .is("completed_at", null)
    .in("type", ["new_request", "new_comment", "new_file", "status_change"])
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) {
    logger.warn("findActiveLockTask failed:", error.message);
    return null;
  }
  if (data && data.length > 0) return data[0] as unknown as RequestTaskRow;
  return null;
}

// The active (uncompleted) picker task for a request scoped to ONE Sales Order
// (option (b): a picker works one SO at a time). Used by the picker take-on gate
// to know whether the opener must claim the order (unassigned) or it is held by
// another picker. null on error / no client / none found.
export async function findPickerLockTask(
  requestId: number | string,
  soNumber: string
): Promise<RequestTaskRow | null> {
  const c = getTypedClient();
  if (!c) return null;
  const { data, error } = await c
    .from("request_tasks")
    .select("id, assigned_to, assigned_to_id, assigned_at, type, completed_at, so_number")
    .eq("request_id", requestId as number)
    .eq("so_number", soNumber)
    .is("completed_at", null)
    .in("type", PICKER_TASK_TYPES)
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) {
    logger.warn("findPickerLockTask failed:", error.message);
    return null;
  }
  if (data && data.length > 0) return data[0] as unknown as RequestTaskRow;
  return null;
}

// --- writes ---

// Insert a work item (createTask / createPickerTask). Loose row cast internally.
export async function insertTask(row: object): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c.from("request_tasks").insert(row as RequestTaskInsert);
  if (error) logger.warn("insertTask failed:", error.message);
  return { error };
}

// Patch a task by id (no row returned) — the take-on-lock claim.
export async function updateTaskById(
  id: string | number,
  patch: object
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("request_tasks")
    .update(patch as RequestTaskUpdate)
    .eq("id", id as string);
  if (error) logger.warn("updateTaskById failed:", error.message);
  return { error };
}

// Patch a task by id and return the updated row (the to-do / picker list actions
// that re-render the row from the response).
export async function updateTaskByIdReturning(
  id: string | number,
  patch: object
): Promise<{ data: RequestTaskRow | null; error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  const { data, error } = await c
    .from("request_tasks")
    .update(patch as RequestTaskUpdate)
    .eq("id", id as string)
    .select()
    .single();
  if (error) {
    logger.warn("updateTaskByIdReturning failed:", error.message);
    return { data: null, error };
  }
  return { data: data as unknown as RequestTaskRow, error: null };
}

// Cascade update for ALL of a request's RESPONDER tasks at once (the Responders
// Task List acts at the request level: Take On / Assign / Done / Release / Reopen
// apply to every task of the request, not just the clicked row). Picker-only
// types are never touched. `scope` selects which rows to hit by completion state:
//   "open"      → completed_at IS NULL   (Take On / Assign / Done / Release)
//   "completed" → completed_at IS NOT NULL (Reopen)
// Returns the updated rows so the caller can patch local state.
export async function updateResponderTasksByRequest(
  requestId: number | string,
  patch: object,
  scope: "open" | "completed" = "open"
): Promise<{ data: RequestTaskRow[] | null; error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  let q = c
    .from("request_tasks")
    .update(patch as RequestTaskUpdate)
    .eq("request_id", requestId as number)
    .not("type", "in", NON_RESPONDER_IN);
  q = scope === "open" ? q.is("completed_at", null) : q.not("completed_at", "is", null);
  const { data, error } = await q.select();
  if (error) {
    logger.warn("updateResponderTasksByRequest failed:", error.message);
    return { data: null, error };
  }
  return { data: (data ?? []) as unknown as RequestTaskRow[], error: null };
}

// Mark THIS user's picker tasks for a request as Actioned — the FALLBACK for a picker
// view opened without an SO scope (no ?picker_so). Touches only picker-only types
// assigned to this user + not yet completed, so the db-60 guard passes. Returns the rows.
export async function completePickerTasksForRequest(
  requestId: number | string,
  userId: string,
  completedAt: string
): Promise<{ data: RequestTaskRow[] | null; error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  const { data, error } = await c
    .from("request_tasks")
    .update({ completed_at: completedAt } as RequestTaskUpdate)
    .eq("request_id", requestId as number)
    .in("type", PICKER_TASK_TYPES)
    .eq("assigned_to_id", userId)
    .is("completed_at", null)
    .select();
  if (error) {
    logger.warn("completePickerTasksForRequest failed:", error.message);
    return { data: null, error };
  }
  return { data: (data ?? []) as unknown as RequestTaskRow[], error: null };
}

// Mark the ONE picker task for a request identified by its SO number as Actioned —
// the picker view's "Completed" button, option (b): a picker works and actions one SO
// at a time. Assignee-scoped so the db-60 guard passes and a co-picker's task on the
// same request (a different SO) is left alone. Returns the updated row(s).
export async function completePickerTaskBySoNumber(
  requestId: number | string,
  soNumber: string,
  userId: string,
  completedAt: string
): Promise<{ data: RequestTaskRow[] | null; error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { data: null, error: null };
  const { data, error } = await c
    .from("request_tasks")
    .update({ completed_at: completedAt } as RequestTaskUpdate)
    .eq("request_id", requestId as number)
    .eq("so_number", soNumber)
    .eq("assigned_to_id", userId)
    .is("completed_at", null)
    .select();
  if (error) {
    logger.warn("completePickerTaskBySoNumber failed:", error.message);
    return { data: null, error };
  }
  return { data: (data ?? []) as unknown as RequestTaskRow[], error: null };
}

// Auto-complete the open tasks of the given types for a request ref (on approval
// confirm). Leaves picker-only tasks untouched (caller passes the type list).
export async function completeTasksForRef(
  refNo: string,
  types: string[],
  completedAt: string
): Promise<{ error: { message: string } | null }> {
  const c = getTypedClient();
  if (!c) return { error: null };
  const { error } = await c
    .from("request_tasks")
    .update({ completed_at: completedAt })
    .eq("ref_no", refNo)
    .in("type", types)
    .is("completed_at", null);
  if (error) logger.warn("completeTasksForRef failed:", error.message);
  return { error };
}
